/** Display names only. Never use these labels for authorization, filtering or procedure selection. */
export const CASE_TYPE_DEFAULT_LABELS: Readonly<Record<string, string>> = Object.freeze({
  bankruptcy_dohai: '自己破産(同廃)',
  bankruptcy_kanzai: '自己破産(管財)',
  individual_rehabilitation: '個人再生',
  civil_rehab_no_housing: '個人再生（住宅ローン特則なし）',
  civil_rehab_housing: '個人再生（住宅ローン特則あり）',
  overpayment: '過払い金',
  voluntary_arrangement: '任意整理',
  labor: '労働問題',
  divorce: '離婚',
  traffic: '交通事故',
  inheritance: '相続',
  corporate: '法人案件',
  other: 'その他',
});

const DISPLAY_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  bankruptcy_simultaneous: 'bankruptcy_dohai',
  bankruptcy_managed: 'bankruptcy_kanzai',
  civil_rehabilitation: 'individual_rehabilitation',
  corporate_bankruptcy: 'corporate',
});
export interface CaseTypeLabelSetting {
  value?: unknown;
  labelOverrides?: unknown;
  labelVersion?: unknown;
}
export interface CaseTypeNames {
  types: { id: string; label: string; defaultLabel: string; overridden: boolean }[];
  labels: Record<string, string>;
  defaultLabels: Record<string, string>;
  labelOverrides: Record<string, string>;
  version: number;
}
function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function own(map: Readonly<Record<string, unknown>>, key: string): boolean {
  return Object.hasOwn(map, key);
}
/** Historical master IDs remain readable but never become newly selectable options. */
export function caseTypeDefaults(setting?: CaseTypeLabelSetting): Record<string, string> {
  const labels: Record<string, string> = { ...CASE_TYPE_DEFAULT_LABELS };
  if (Array.isArray(setting?.value)) {
    for (const entry of setting.value) {
      if (
        record(entry) &&
        typeof entry.id === 'string' &&
        entry.id &&
        typeof entry.label === 'string' &&
        entry.label.trim() &&
        !own(labels, entry.id) &&
        !own(DISPLAY_ALIASES, entry.id) &&
        !['__proto__', 'prototype', 'constructor'].includes(entry.id)
      )
        labels[entry.id] = entry.label;
    }
  }
  return labels;
}
/** Throws for malformed writes; callers return 400. An empty map resets all names. */
export function validateCaseTypeOverrides(
  value: unknown,
  setting?: CaseTypeLabelSetting,
): Record<string, string> {
  if (!record(value)) throw new Error('labelOverrides must be an object');
  const defaults = caseTypeDefaults(setting);
  const result: Record<string, string> = {};
  for (const [id, name] of Object.entries(value)) {
    if (
      !own(defaults, id) ||
      typeof name !== 'string' ||
      !name.trim() ||
      name.trim().length > 80 ||
      [...name].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    ) {
      throw new Error('Invalid case type display name');
    }
    if (name.trim() !== defaults[id]) result[id] = name.trim();
  }
  return result;
}
/** No global mutable state: every resolution is bound to one organization's snapshot. */
export function resolveCaseTypeNames(setting?: CaseTypeLabelSetting): CaseTypeNames {
  const defaults = caseTypeDefaults(setting);
  const overrides: Record<string, string> = {};
  if (record(setting?.labelOverrides)) {
    for (const [id, name] of Object.entries(setting.labelOverrides)) {
      if (own(defaults, id) && typeof name === 'string' && name.trim()) overrides[id] = name;
    }
  }
  return {
    types: Object.entries(CASE_TYPE_DEFAULT_LABELS).map(([id, defaultLabel]) => ({
      id,
      defaultLabel,
      label: overrides[id] ?? defaultLabel,
      overridden: own(overrides, id),
    })),
    labels: { ...defaults, ...overrides },
    defaultLabels: defaults,
    labelOverrides: overrides,
    version:
      typeof setting?.labelVersion === 'number' &&
      Number.isSafeInteger(setting.labelVersion) &&
      setting.labelVersion >= 0
        ? setting.labelVersion
        : 0,
  };
}
export function resolveCaseTypeLabel(
  value: string | null | undefined,
  names: CaseTypeNames,
  fallback = '事件種別未設定',
): string {
  if (!value) return fallback;
  const key = own(DISPLAY_ALIASES, value) ? DISPLAY_ALIASES[value] : value;
  if (own(names.labels, key)) return names.labels[key];
  const oldKey = Object.keys(names.defaultLabels).find((id) => names.defaultLabels[id] === value);
  return oldKey ? names.labels[oldKey] : fallback;
}

/** English defaults are supplied by the existing locale dictionary; office names are never translated. */
export function resolveLocalizedCaseTypeLabel(
  value: string | null | undefined,
  names: CaseTypeNames,
  locale: 'ja' | 'en',
  englishDefaults: Readonly<Record<string, string>>,
  fallback = locale === 'ja' ? '事件種別未設定' : 'Case type not set',
): string {
  if (!value) return fallback;
  const alias = own(DISPLAY_ALIASES, value) ? DISPLAY_ALIASES[value] : value;
  const key = own(names.labels, alias)
    ? alias
    : Object.keys(names.defaultLabels).find((id) => names.defaultLabels[id] === value);
  if (!key) return fallback;
  if (own(names.labelOverrides, key)) return names.labelOverrides[key];
  return locale === 'en' && own(englishDefaults, key) ? englishDefaults[key] : names.labels[key];
}

export interface CaseTypeLabelWrite {
  labelOverrides: Record<string, string>;
  expectedVersion: number;
  nextVersion: number;
}

/** Only these attributes may be updated. The old value/master/order and unrelated settings are untouched.
 * The future writer MUST condition its DynamoDB update on labelVersion (missing = version zero),
 * and transact the settings update with its audit record. A prior read alone is insufficient.
 */
export function prepareCaseTypeLabelWrite(
  body: unknown,
  setting?: CaseTypeLabelSetting,
): CaseTypeLabelWrite {
  if (
    !record(body) ||
    Object.keys(body).some((key) => !['labelOverrides', 'expectedVersion'].includes(key)) ||
    typeof body.expectedVersion !== 'number' ||
    !Number.isSafeInteger(body.expectedVersion) ||
    body.expectedVersion < 0 ||
    body.expectedVersion >= Number.MAX_SAFE_INTEGER
  ) {
    throw new Error('Invalid display name update');
  }
  if (
    setting?.labelVersion !== undefined &&
    (typeof setting.labelVersion !== 'number' ||
      !Number.isSafeInteger(setting.labelVersion) ||
      setting.labelVersion < 0)
  ) {
    throw new Error('Invalid stored display name version');
  }
  const currentVersion = setting?.labelVersion ?? 0;
  if (body.expectedVersion !== currentVersion) throw new Error('Display name version conflict');
  return {
    labelOverrides: validateCaseTypeOverrides(body.labelOverrides, setting),
    expectedVersion: body.expectedVersion,
    nextVersion: body.expectedVersion + 1,
  };
}
